import React, { useEffect, useState } from "react";
import "./sellhouse.css";
import { useNavigate } from "react-router-dom";
import API from "../../../api";
import {
  ArrowLeft,
  X,
  Home,
  MapPin,
  IndianRupee,
  ImagePlus,
  FileText,
  Lightbulb,
  ShieldCheck,
  Upload,
  CheckCircle2,
  Loader2,
} from "lucide-react";

function SellHouse() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);

  /* =================================================
     CLEAN IMAGE PREVIEW URL
  ================================================= */

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  /* =================================================
     IMAGE HANDLER
  ================================================= */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setImageFile(file);

    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setPreview(URL.createObjectURL(file));
  };

  /* =================================================
     SUBMIT
  ================================================= */

  const handleSubmit = async () => {
    if (!localStorage.getItem("token")) {
      alert("Please login again");
      return;
    }

    if (!title || !city || !price) {
      alert("Title, city and price are required");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("title", title);
      formData.append("city", city);
      formData.append("state", state);
      formData.append("price", price);
      formData.append("description", description);

      if (imageFile) {
        formData.append("image", imageFile);
      }

      const res = await API.post(
        "/house/sell",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        alert("House posted successfully");
        navigate("/home");
      } else {
        alert(
          res.data.message ||
            "Failed to post house"
        );
      }
    } catch (err) {
      console.error(
        "SELL HOUSE ERROR:",
        err
      );

      alert(
        "Server error. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =================================================
     REMOVE IMAGE
  ================================================= */

  const removeImage = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setPreview(null);
    setImageFile(null);
  };

  return (
    <div className="sell-page">

      {/* =================================================
          TOP BAR
      ================================================= */}

      <div className="sell-topbar">

        <button
          className="sell-back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />

          <span>
            Back
          </span>
        </button>

        <button
          className="sell-close-btn"
          onClick={() => navigate("/")}
          aria-label="Close"
        >
          <X size={19} />
        </button>

      </div>


      {/* =================================================
          PAGE CONTENT
      ================================================= */}

      <main className="sell-main">

        {/* =================================================
            LEFT INTRO
        ================================================= */}

        <section className="sell-intro">

          <div className="sell-intro-badge">
            <Home size={15} />
            HOUSE LISTING
          </div>

          <h1>
            Sell your house
            <br />
            <span>with confidence.</span>
          </h1>

          <p>
            Create a clear and accurate listing
            to help potential buyers understand
            your property better.
          </p>


          {/* =================================================
              LISTING BENEFITS
          ================================================= */}

          <div className="sell-benefits">

            <div className="sell-benefit">

              <div className="sell-benefit-icon">
                <CheckCircle2 size={17} />
              </div>

              <div>
                <strong>
                  Clear Property Details
                </strong>

                <span>
                  Give buyers useful information
                  about your house.
                </span>
              </div>

            </div>


            <div className="sell-benefit">

              <div className="sell-benefit-icon">
                <ImagePlus size={17} />
              </div>

              <div>
                <strong>
                  Add Property Photos
                </strong>

                <span>
                  Help buyers understand the
                  property before contacting you.
                </span>
              </div>

            </div>


            <div className="sell-benefit">

              <div className="sell-benefit-icon">
                <ShieldCheck size={17} />
              </div>

              <div>
                <strong>
                  Be Accurate
                </strong>

                <span>
                  Accurate information builds
                  trust with potential buyers.
                </span>
              </div>

            </div>

          </div>


          {/* =================================================
              LISTING TIP
          ================================================= */}

          <div className="sell-tip">

            <div className="sell-tip-icon">
              <Lightbulb size={18} />
            </div>

            <div>

              <strong>
                Listing tip
              </strong>

              <p>
                Mention important details such as
                house type, locality, amenities,
                nearby facilities and property age
                in your description.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FORM CARD
        ================================================= */}

        <section className="sell-card">

          {/* =================================================
              FORM HEADER
          ================================================= */}

          <div className="sell-card-header">

            <div className="sell-card-icon">
              <Home size={20} />
            </div>

            <div>

              <span>
                CREATE LISTING
              </span>

              <h2>
                Property Details
              </h2>

              <p>
                Add the basic information about
                your house.
              </p>

            </div>

          </div>


          {/* =================================================
              BASIC DETAILS
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <FileText size={16} />

              <span>
                Basic Information
              </span>

            </div>


            {/* TITLE */}

            <div className="sell-form-group">

              <label>
                House Title
                <span>*</span>
              </label>

              <div className="sell-input-wrapper">

                <Home size={17} />

                <input
                  value={title}
                  onChange={(e) =>
                    setTitle(e.target.value)
                  }
                  placeholder="e.g. 3BHK Independent House"
                />

              </div>

            </div>


            {/* LOCATION */}

            <div className="sell-form-row">

              <div className="sell-form-group">

                <label>
                  City
                  <span>*</span>
                </label>

                <div className="sell-input-wrapper">

                  <MapPin size={17} />

                  <input
                    value={city}
                    onChange={(e) =>
                      setCity(e.target.value)
                    }
                    placeholder="City"
                  />

                </div>

              </div>


              <div className="sell-form-group">

                <label>
                  State
                </label>

                <div className="sell-input-wrapper">

                  <MapPin size={17} />

                  <input
                    value={state}
                    onChange={(e) =>
                      setState(e.target.value)
                    }
                    placeholder="State"
                  />

                </div>

              </div>

            </div>


            {/* PRICE */}

            <div className="sell-form-group">

              <label>
                Expected Price
                <span>*</span>
              </label>

              <div className="sell-price-input">

                <div className="sell-price-symbol">
                  <IndianRupee size={17} />
                </div>

                <input
                  type="number"
                  value={price}
                  onChange={(e) =>
                    setPrice(e.target.value)
                  }
                  placeholder="Enter expected price"
                />

              </div>

              {price && (
                <span className="sell-price-preview">

                  ₹
                  {Number(
                    price
                  ).toLocaleString(
                    "en-IN"
                  )}

                </span>
              )}

            </div>

          </div>


          {/* =================================================
              IMAGE
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <ImagePlus size={16} />

              <span>
                Property Image
              </span>

            </div>


            {!preview ? (

              <label className="sell-upload-area">

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />

                <div className="sell-upload-icon">
                  <Upload size={21} />
                </div>

                <strong>
                  Upload a house image
                </strong>

                <span>
                  Choose a clear photo of your
                  property
                </span>

                <small>
                  JPG, PNG or other image formats
                </small>

              </label>

            ) : (

              <div className="sell-preview-container">

                <img
                  src={preview}
                  alt="House preview"
                  className="sell-image-preview"
                />

                <div className="sell-preview-overlay">

                  <div>
                    <ImagePlus size={16} />

                    <span>
                      {imageFile?.name ||
                        "Property image"}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={removeImage}
                  >
                    <X size={16} />
                    Remove
                  </button>

                </div>

              </div>

            )}

          </div>


          {/* =================================================
              DESCRIPTION
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <FileText size={16} />

              <span>
                Property Description
              </span>

            </div>

            <div className="sell-form-group">

              <label>
                Description
              </label>

              <textarea
                value={description}
                onChange={(e) =>
                  setDescription(
                    e.target.value
                  )
                }
                placeholder="Describe your house, amenities, age, nearby facilities..."
              />

              <div className="sell-character-count">
                {description.length} characters
              </div>

            </div>

          </div>


          {/* =================================================
              DISCLAIMER
          ================================================= */}

          <div className="sell-disclaimer">

            <ShieldCheck size={18} />

            <p>
              Make sure the information you provide
              is accurate. Civilink is a listing
              platform and does not guarantee or
              legally verify property ownership,
              documents or other property details.
            </p>

          </div>


          {/* =================================================
              SUBMIT
          ================================================= */}

          <button
            className="post-btn"
            onClick={handleSubmit}
            disabled={loading}
          >

            {loading ? (

              <>
                <Loader2
                  size={18}
                  className="sell-loading-icon"
                />

                Posting House...
              </>

            ) : (

              <>
                <CheckCircle2 size={18} />

                Post House
              </>

            )}

          </button>


          <p className="sell-required-note">
            Fields marked with * are required.
          </p>

        </section>

      </main>

    </div>
  );
}

export default SellHouse;